import { useEffect, useState } from "react";
import api, { apiError, fmtUSD } from "@/lib/api";
import { Panel, Table, Money, Metric, Empty } from "@/components/terminal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "@/components/ui/sonner";

const SOURCES = ["FOUNDER_CONTRIBUTION", "BUSINESS_REVENUE", "ASSET_SALE", "DIVIDEND", "INTEREST", "CRYPTO_REWARD", "STAKING_REWARD", "CARD_REWARD", "REFERRAL_INCOME", "SECURITIES_LENDING", "REALIZED_TRADING_PROFIT", "OTHER_INCOME"];

export default function Cash() {
  const [data, setData] = useState(null);
  const [inflows, setInflows] = useState([]);
  const [source, setSource] = useState("FOUNDER_CONTRIBUTION");
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/buckets").then((r) => setData(r.data)).catch(() => {});
    api.get("/inflows").then((r) => setInflows(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/inflows", { source_type: source, amount: parseFloat(amount) });
      toast.success("Inflow recorded and routed");
      setAmount("");
      load();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  if (!data) return <div className="text-slate-500 font-mono text-sm">Loading…</div>;

  return (
    <div className="space-y-5" data-testid="cash-page">
      <h2 className="font-display text-xl font-bold">Cash & Capital Buckets</h2>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <Metric label="Cash Available" value={fmtUSD(data.cash_available)} testId="cash-available" />
        <Metric label="Reserved For Taxes (locked)" value={fmtUSD(data.cash_reserved_taxes)} tone="gold" testId="cash-tax-locked" />
        <Metric label="Deployable Capital" value={fmtUSD(data.deployable_capital)} testId="cash-deployable" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Panel title="Capital Buckets" testId="capital-buckets-allocator" className="lg:col-span-2">
          <Table testId="buckets-table" head={[{ label: "Bucket" }, { label: "Balance", num: true }, { label: "Min Reserve", num: true }, { label: "Deployable" }, { label: "Purpose" }]}>
            {data.buckets.map((b) => (
              <tr key={b.key} className="hover:bg-[#222630]" data-testid={`bucket-row-${b.key}`}>
                <td className="td-cell font-semibold text-slate-100">{b.name}</td>
                <td className="td-num"><Money value={b.balance} className={b.key === "tax_reserve" ? "text-amber-400" : ""} /></td>
                <td className="td-num">{fmtUSD(b.min_reserve, 0)}</td>
                <td className="td-cell">{b.deployable ? <span className="text-emerald-400 text-[11px] font-semibold">YES</span> : <span className="text-red-400 text-[11px] font-semibold">NEVER</span>}</td>
                <td className="td-cell text-slate-500">{b.description}</td>
              </tr>
            ))}
          </Table>
          <p className="text-[10px] text-slate-600 mt-3">The tax reserve is never counted as investable capital. Deployable capital = deployable bucket balances above their reserve floors.</p>
        </Panel>
        <Panel title="Record Capital Inflow" testId="inflow-form-panel">
          <form onSubmit={submit} className="space-y-3">
            <div className="metric-label">Source</div>
            <Select value={source} onValueChange={setSource}>
              <SelectTrigger data-testid="inflow-source-select" className="bg-[#121316] border-[#2D323E]"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#17191E] border-[#2D323E]">
                {SOURCES.map((s) => <SelectItem key={s} value={s} data-testid={`inflow-source-${s}`}>{s.replace(/_/g, " ")}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="metric-label">Amount (USD)</div>
            <Input data-testid="inflow-amount-input" type="number" min="0.01" step="0.01" required value={amount}
              onChange={(e) => setAmount(e.target.value)} className="bg-[#121316] border-[#2D323E] font-mono" />
            <Button data-testid="inflow-submit-button" type="submit" disabled={busy} className="w-full bg-[#D4AF37] hover:bg-[#C5A059] text-[#0B0C0E] font-bold">
              {busy ? "ROUTING…" : "RECORD & ROUTE"}
            </Button>
            <p className="text-[10px] text-slate-600">Pipeline: New Capital → Tax Assessment → Tax Reserve → Operating Requirement → Allocation Engine → Investment / Reserve.</p>
          </form>
        </Panel>
      </div>
      <Panel title="Inflow History" testId="inflow-history">
        {inflows.length === 0 ? <Empty>No inflows recorded.</Empty> : (
          <Table testId="inflows-table" head={[{ label: "Time" }, { label: "Source" }, { label: "Amount", num: true }, { label: "Tax Reserved", num: true }, { label: "Routing" }]}>
            {inflows.map((i) => (
              <tr key={i.id} className="hover:bg-[#222630]">
                <td className="td-cell font-mono">{new Date(i.timestamp).toLocaleString()}</td>
                <td className="td-cell">{i.source_type.replace(/_/g, " ")}</td>
                <td className="td-num">{fmtUSD(i.amount)}</td>
                <td className="td-num text-amber-400">{fmtUSD(i.tax_reserved)}</td>
                <td className="td-cell font-mono text-[10px] text-slate-500">
                  {Object.entries(i.routing || {}).map(([k, v]) => `${k}: $${v.toFixed(2)}`).join(" · ")}
                </td>
              </tr>
            ))}
          </Table>
        )}
      </Panel>
    </div>
  );
}
